// @ts-nocheck
import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
} from 'recharts';
import api from '../services/api';
import { useI18n } from '../context/I18nContext';
import { useTheme } from '../context/ThemeContext';

const PIE_COLORS = ['#198754', '#0d6efd', '#ffc107', '#6c757d', '#dc3545'];

function fmtMoney(n) {
    const v = Number(n || 0);
    if (v >= 10000000) return (v / 10000000).toFixed(2) + ' Cr';
    if (v >= 100000) return (v / 100000).toFixed(2) + ' L';
    return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function StatCard({ label, value, sub, icon, tone, link }) {
    const tones = {
        green: { bg: 'rgba(25,135,84,.12)', color: '#198754' },
        blue: { bg: 'rgba(13,110,253,.12)', color: '#0d6efd' },
        amber: { bg: 'rgba(255,193,7,.15)', color: '#b8860b' },
        red: { bg: 'rgba(220,53,69,.12)', color: '#dc3545' },
        teal: { bg: 'rgba(32,201,151,.12)', color: '#20c997' },
        purple: { bg: 'rgba(111,66,193,.12)', color: '#6f42c1' },
    };
    const t = tones[tone] || tones.green;
    const inner = (
        <div className="card stat-card h-100 border-0 shadow-sm">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                        <div className="text-muted small text-uppercase fw-semibold" style={{ letterSpacing: 0.4 }}>
                            {label}
                        </div>
                        <div className="fs-4 fw-bold mt-1" style={{ color: 'var(--text)' }}>
                            {value ?? '—'}
                        </div>
                        {sub != null && sub !== '' && (
                            <div className="small text-muted mt-1">{sub}</div>
                        )}
                    </div>
                    <div
                        className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 44, height: 44, background: t.bg, color: t.color }}
                    >
                        <i className={`bi ${icon} fs-5`} />
                    </div>
                </div>
            </div>
        </div>
    );
    if (link) {
        return (
            <Link to={link} className="text-decoration-none">
                {inner}
            </Link>
        );
    }
    return inner;
}

export default function Dashboard() {
    const { lang, t } = useI18n();
    const { mode } = useTheme();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api
            .get('/payments/stats/dashboard')
            .then((res) => setStats(res.data.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const isDark = mode === 'dark';
    const gridStroke = isDark ? '#2d3340' : '#e9ecef';
    const tickFill = isDark ? '#9aa0a6' : '#6c757d';
    const tooltipStyle = {
        backgroundColor: isDark ? '#1a1d26' : '#fff',
        border: `1px solid ${isDark ? '#2d3340' : '#dee2e6'}`,
        borderRadius: 8,
        color: isDark ? '#e8eaed' : '#212529',
    };

    const collectionChart = useMemo(() => {
        if (!stats?.chart) return [];
        return stats.chart.months.map((m, i) => ({
            month: m,
            collections: stats.chart.collections[i] || 0,
            bookings: stats.chart.bookings[i] || 0,
        }));
    }, [stats]);

    const unitPie = useMemo(() => {
        return (stats?.unitsByStatus || []).filter((u) => u.value > 0);
    }, [stats]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-success" />
                <div className="text-muted mt-2 small">Loading dashboard…</div>
            </div>
        );
    }

    const title = t('dashboard');
    const subtitle =
        t('dashboardDesc');

    return (
        <div className="dashboard-page">
            <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-4">
                <div>
                    <h4 className="mb-1 fw-bold">{title}</h4>
                    <p className="text-muted mb-0">{subtitle}</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                    <Link to="/bookings" className="btn btn-primary btn-sm">
                        <i className="bi bi-journal-plus me-1" />
                        {t('newBooking')}
                    </Link>
                    <Link to="/payments" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-cash me-1" />
                        {t('payment')}
                    </Link>
                    <Link to="/accounting-reports" className="btn btn-outline-secondary btn-sm">
                        <i className="bi bi-graph-up me-1" />
                        Reports
                    </Link>
                </div>
            </div>

            {/* KPI row */}
            <div className="row g-3 mb-3">
                <div className="col-6 col-md-4 col-xl-3">
                    <StatCard
                        label={t('projects')}
                        value={stats?.totalProjects}
                        icon="bi-building"
                        tone="green"
                        link="/projects"
                    />
                </div>
                <div className="col-6 col-md-4 col-xl-3">
                    <StatCard
                        label={t('totalUnits')}
                        value={stats?.totalUnits}
                        sub={`${stats?.availableUnits || 0} available`}
                        icon="bi-grid-3x3-gap"
                        tone="blue"
                        link="/units"
                    />
                </div>
                <div className="col-6 col-md-4 col-xl-3">
                    <StatCard
                        label={t('bookings')}
                        value={stats?.totalBookings}
                        sub={`${stats?.pendingBookings || 0} pending`}
                        icon="bi-journal-check"
                        tone="amber"
                        link="/bookings"
                    />
                </div>
                <div className="col-6 col-md-4 col-xl-3">
                    <StatCard
                        label={t('collected')}
                        value={fmtMoney(stats?.totalCollected)}
                        sub={stats?.pendingPayments ? `${stats.pendingPayments} pending approve` : 'All clear'}
                        icon="bi-cash-coin"
                        tone="teal"
                        link="/payments"
                    />
                </div>
                <div className="col-6 col-md-4 col-xl-3">
                    <StatCard
                        label={t('overdue')}
                        value={stats?.overdueInstallments}
                        icon="bi-exclamation-triangle"
                        tone="red"
                        link="/payments"
                    />
                </div>
                <div className="col-6 col-md-4 col-xl-3">
                    <StatCard
                        label={t('occupancySold')}
                        value={`${stats?.occupancyRate || 0}%`}
                        sub={`${(stats?.bookedUnits || 0) + (stats?.soldUnits || 0)} booked+sold`}
                        icon="bi-pie-chart"
                        tone="purple"
                        link="/units"
                    />
                </div>
                <div className="col-6 col-md-4 col-xl-3">
                    <StatCard
                        label={t('leads')}
                        value={stats?.totalLeads}
                        sub={`${stats?.openLeads || 0} open`}
                        icon="bi-people"
                        tone="blue"
                        link="/leads"
                    />
                </div>
                <div className="col-6 col-md-4 col-xl-3">
                    <StatCard
                        label={t('stockValue')}
                        value={fmtMoney(stats?.stockValue)}
                        sub={stats?.lowStock ? `${stats.lowStock} low stock` : 'Stock healthy'}
                        icon="bi-boxes"
                        tone={stats?.lowStock ? 'amber' : 'green'}
                        link="/materials"
                    />
                </div>
            </div>

            {/* Charts */}
            <div className="row g-3 mb-3">
                <div className="col-lg-8">
                    <div className="card table-card h-100 border-0 shadow-sm">
                        <div className="card-header border-0 bg-transparent d-flex justify-content-between align-items-center">
                            <div>
                                <div className="fw-semibold">
                                    {t('collectionsBookingsChart')}
                                </div>
                                <div className="small text-muted">Approved payments vs new bookings</div>
                            </div>
                        </div>
                        <div className="card-body pt-0" style={{ minHeight: 280 }}>
                            <ResponsiveContainer width="100%" height={260}>
                                <AreaChart data={collectionChart} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="colGrad" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor="#198754" stopOpacity={0.35} />
                                            <stop offset="95%" stopColor="#198754" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                                    <XAxis dataKey="month" tick={{ fill: tickFill, fontSize: 11 }} />
                                    <YAxis
                                        yAxisId="left"
                                        tick={{ fill: tickFill, fontSize: 11 }}
                                        tickFormatter={(v) => fmtMoney(v)}
                                    />
                                    <YAxis
                                        yAxisId="right"
                                        orientation="right"
                                        tick={{ fill: tickFill, fontSize: 11 }}
                                        allowDecimals={false}
                                    />
                                    <Tooltip
                                        contentStyle={tooltipStyle}
                                        formatter={(value, name) =>
                                            name === 'collections'
                                                ? [Number(value).toLocaleString(), 'Collections']
                                                : [value, 'Bookings']
                                        }
                                    />
                                    <Legend />
                                    <Area
                                        yAxisId="left"
                                        type="monotone"
                                        dataKey="collections"
                                        name="collections"
                                        stroke="#198754"
                                        fill="url(#colGrad)"
                                        strokeWidth={2}
                                    />
                                    <Bar
                                        yAxisId="right"
                                        dataKey="bookings"
                                        name="bookings"
                                        fill="#0d6efd"
                                        radius={[4, 4, 0, 0]}
                                        maxBarSize={28}
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="card table-card h-100 border-0 shadow-sm">
                        <div className="card-header border-0 bg-transparent">
                            <div className="fw-semibold">
                                {t('unitInventoryMix')}
                            </div>
                            <div className="small text-muted">Available · Booked · Sold</div>
                        </div>
                        <div className="card-body d-flex flex-column align-items-center justify-content-center">
                            {unitPie.length === 0 ? (
                                <div className="text-muted small py-4">No unit data</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie
                                            data={unitPie}
                                            dataKey="value"
                                            nameKey="name"
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={55}
                                            outerRadius={85}
                                            paddingAngle={3}
                                        >
                                            {unitPie.map((_, i) => (
                                                <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={tooltipStyle} />
                                        <Legend />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                            <div className="w-100 mt-1">
                                <div className="d-flex justify-content-between small">
                                    <span className="text-muted">Occupancy</span>
                                    <span className="fw-semibold">{stats?.occupancyRate || 0}%</span>
                                </div>
                                <div className="progress mt-1" style={{ height: 8 }}>
                                    <div
                                        className="progress-bar bg-success"
                                        style={{ width: `${Math.min(100, stats?.occupancyRate || 0)}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Secondary charts + finance snapshot */}
            <div className="row g-3 mb-3">
                <div className="col-lg-4">
                    <div className="card table-card h-100 border-0 shadow-sm">
                        <div className="card-header border-0 bg-transparent fw-semibold">
                            {t('financeSnapshot')}
                        </div>
                        <div className="card-body">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Collections</span>
                                <span className="fw-semibold text-success">৳ {Number(stats?.totalCollected || 0).toLocaleString()}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Expenses (approved)</span>
                                <span className="fw-semibold text-danger">৳ {Number(stats?.expenseTotal || 0).toLocaleString()}</span>
                            </div>
                            <hr className="my-2" />
                            <div className="d-flex justify-content-between">
                                <span className="fw-semibold">Net (coll. − exp.)</span>
                                <span className="fw-bold">৳ {Number(stats?.collectionNet || 0).toLocaleString()}</span>
                            </div>
                            <div className="mt-3">
                                <ResponsiveContainer width="100%" height={120}>
                                    <BarChart
                                        data={[
                                            { name: 'In', amount: stats?.totalCollected || 0 },
                                            { name: 'Out', amount: stats?.expenseTotal || 0 },
                                        ]}
                                    >
                                        <XAxis dataKey="name" tick={{ fill: tickFill, fontSize: 11 }} />
                                        <YAxis hide />
                                        <Tooltip
                                            contentStyle={tooltipStyle}
                                            formatter={(v) => Number(v).toLocaleString()}
                                        />
                                        <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                                            <Cell fill="#198754" />
                                            <Cell fill="#dc3545" />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="card table-card h-100 border-0 shadow-sm">
                        <div className="card-header border-0 bg-transparent d-flex justify-content-between">
                            <span className="fw-semibold">{t('recentPayments')}</span>
                            <Link to="/payments" className="small">View all</Link>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Customer</th>
                                            <th className="text-end">Amount</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(stats?.recentPayments || []).length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="text-muted text-center py-3 small">
                                                    No payments yet
                                                </td>
                                            </tr>
                                        )}
                                        {(stats?.recentPayments || []).map((p) => (
                                            <tr key={p.id}>
                                                <td className="small">{p.payment_date}</td>
                                                <td className="small">{p.customer?.name || '—'}</td>
                                                <td className="text-end small">
                                                    {Number(p.amount).toLocaleString()}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`badge ${
                                                            p.status === 'approved'
                                                                ? 'bg-success'
                                                                : p.status === 'pending'
                                                                  ? 'bg-warning text-dark'
                                                                  : 'bg-secondary'
                                                        }`}
                                                    >
                                                        {p.status}
                                                    </span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-lg-4">
                    <div className="card table-card h-100 border-0 shadow-sm">
                        <div className="card-header border-0 bg-transparent d-flex justify-content-between">
                            <span className="fw-semibold">{t('recentBookings')}</span>
                            <Link to="/bookings" className="small">View all</Link>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                    <thead>
                                        <tr>
                                            <th>No</th>
                                            <th>Customer</th>
                                            <th>Project</th>
                                            <th className="text-end">Price</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(stats?.recentBookings || []).length === 0 && (
                                            <tr>
                                                <td colSpan={4} className="text-muted text-center py-3 small">
                                                    No bookings yet
                                                </td>
                                            </tr>
                                        )}
                                        {(stats?.recentBookings || []).map((b) => (
                                            <tr key={b.id}>
                                                <td className="small">
                                                    <Link to={`/bookings/${b.id}`}>{b.booking_no}</Link>
                                                </td>
                                                <td className="small">{b.customer?.name || '—'}</td>
                                                <td className="small text-truncate" style={{ maxWidth: 90 }}>
                                                    {b.project?.name || '—'}
                                                </td>
                                                <td className="text-end small">
                                                    {Number(b.final_price || 0).toLocaleString()}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Quick actions */}
            <div className="card table-card border-0 shadow-sm">
                <div className="card-header border-0 bg-transparent fw-semibold">
                    {t('quickActions')}
                </div>
                <div className="card-body d-flex flex-wrap gap-2">
                    <Link to="/projects" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-building me-1" /> Projects
                    </Link>
                    <Link to="/units" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-grid me-1" /> Inventory
                    </Link>
                    <Link to="/leads" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-person-plus me-1" /> Leads
                    </Link>
                    <Link to="/bookings" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-journal-plus me-1" /> Bookings
                    </Link>
                    <Link to="/payments" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-cash me-1" /> Payments
                    </Link>
                    <Link to="/materials" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-boxes me-1" /> Materials
                    </Link>
                    <Link to="/accounting-reports" className="btn btn-outline-primary btn-sm">
                        <i className="bi bi-journal-richtext me-1" /> A/C Reports
                    </Link>
                    <Link to="/advanced" className="btn btn-outline-secondary btn-sm">
                        <i className="bi bi-lightning me-1" /> Advanced
                    </Link>
                </div>
            </div>
        </div>
    );
}
